<script setup>
  import { ref, computed, onMounted, getCurrentInstance, defineEmits, defineProps } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { usePageLoadingStore } from "../../../../../stores/pageLoading";
  import KSASecondaryButton from "../../../../../components/ui-components/button/KSASecondaryButton.vue";
  import KSAPrimaryButton from "../../../../../components/ui-components/button/KSAPrimaryButton.vue";
  import mypageJobFairApplicantService from "../../../../user/services/mypage/jobFair/mypageJobFairApplicantService";
  import adminJobFairResumeService from "../../../services/jobfair/adminJobFairResumeService";

  const emits = defineEmits(['go-back', 'onClickJobNotice', 'onClickResume']);

  const props = defineProps({
    corpId: {
      type: String,
      default: '',
    },
    jobNoticeSeq: {
      type: String,
      default: '',
    },
  });

  const { $pageListBoardNo, $toast, $number, $gbNameKR, $yyyymmddHHmmss, alert, confirm } = getCurrentInstance().appContext.config.globalProperties;

  const route = useRoute();
  const router = useRouter();
  const loadingStore = usePageLoadingStore();

  const totalPages = ref(0);
  const totalCount = ref(0);
  const pageSize = ref(10);
  const currentPage = ref(1);
  const list = ref([]);

  const retrieveList = () => {
    let paramObj = {
      jobNoticeSeq: props.jobNoticeSeq,
      pageNumber: currentPage.value,
      size: pageSize.value,
    }
    loadingStore.loading = true;
    adminJobFairResumeService.retrieveResumeListByJobNoticeSeqAdminPaging(paramObj)
      .then( async (res) => {
        loadingStore.loading = false;
        if (res.data.code.toLowerCase() === "success") {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
      }).catch((res) => {
      loadingStore.loading = false;
      $toast("error", res);
    });
  };
    
  // 입사지원 상태변경 joinStateGb
  const onClickApplyStateGb = async (resumeId, jobNoticeSeq, stateGb) => {
    let modalContent, okButtonText;
    if (stateGb === "02") {
      modalContent = ["지원취소 처리하시겠습니까?", "'지원취소'로 상태가 변경됩니다."];
      okButtonText = "지원취소";
    } else if (stateGb === "03") {
      modalContent = ["서류합격 처리하시겠습니까?", "'서류합격'으로 상태가 변경됩니다."];
      okButtonText = "서류합격";
    } else if (stateGb === "04") {
      modalContent = ["서류불합격 처리하시겠습니까?", "'서류불합격'으로 상태가 변경됩니다."];
      okButtonText = "서류불합격";
    } else if (stateGb === "05") {
      modalContent = ["최종합격 처리하시겠습니까?", "'최종합격'으로 상태가 변경됩니다."];
      okButtonText = "최종합격";
    } else if (stateGb === "06") {
      modalContent = ["최종불합격 처리하시겠습니까?", "'최종불합격'으로 상태가 변경됩니다."];
      okButtonText = "최종불합격";
    }

    const ok = await confirm(modalContent, okButtonText, "취소");
    if(ok) {
      const paramObj = {
        resumeId,
        jobNoticeSeq,
        applyStateGb: stateGb,
      };

      mypageJobFairApplicantService.updateApplyStateGb(paramObj)
        .then( (res) => {
          loadingStore.loading = false;
          if(res.data.code.toLowerCase() === 'success') {
            alert("변경하였습니다.");
            retrieveList();
          } else {
            alert("변경을 실패하였습니다.");
          }
        })
        .catch( (res) =>  {
          loadingStore.loading = false;
          alert("변경을 실패하였습니다.");
        });
    }
  }

  const openDetailInNewTab = (linkName, id = {}) => {
    const url = router.resolve({
      name: linkName,
      params: { id },
    }).href;

    window.open(url, '_blank');
  }

  onMounted(()=>{
    retrieveList();
  });
</script>

<template>
  <div>
    <KSASecondaryButton text="이전" @click="emits('go-back')" />
    <div class="list-top">
      <p>총 <span>{{ $number(totalCount)}}</span>명</p>
      <div>
        <KSAPrimaryButton text="채용공고 보기" @click="emits('onClickJobNotice', props.jobNoticeSeq, props.corpId)" />
      </div>
    </div>
    <div class="table-form">
      <table class="horizontal">
        <tr>
          <th>NO</th>
          <th>회원코드</th>
          <th>이름</th>
          <th>아이디</th>
          <th>이력서 제목</th>
          <th>관심 카테고리</th>
          <th>전공계열(주전공)</th>
          <th>입사 지원일</th>
          <th>상태</th>
          <th>상태변경</th>
        </tr>
        <tr v-for="applicant in list">
          <td>{{ applicant.boardNo }}</td>
          <td>{{ applicant.userCd }}</td>
          <td class="clickable blue">
            <span @click="openDetailInNewTab('MemberDetail', applicant.userCd)">{{ applicant.userName }}</span>
          </td>
          <td>{{ applicant.userLoginId }}</td>
          <td class="text-overflow long-overflow">
            <span class="file blue" v-if="applicant.fileYn === 'Y'" v-for="resumeAttaches in applicant.resumeAttaches" @click="$downloadForPrivate({fileName:resumeAttaches.fileName, fileDir: resumeAttaches.fileDir })">
              {{ resumeAttaches.fileName }}
            </span>
            <span class="clickable blue" v-else @click="emits('onClickResume', applicant.resumeId)">
              {{ applicant.title }}
            </span>
          </td>
          <td>
            <span v-if="applicant.fileYn !== 'Y'">{{ applicant.interests }}</span>
            <span v-else>-</span>
          </td>
          <td>{{ $gbNameKR('MAJOR_GB', applicant.majorGb) }}</td>
          <td>{{ $yyyymmddHHmmss(applicant.createDate) }}</td>
          <td>
            <span v-if="applicant.applyStateGb === '01'">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>  
            <span v-else-if="applicant.applyStateGb === '03' || applicant.applyStateGb === '05'" class="blue">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            <span v-else-if="applicant.applyStateGb === '04' || applicant.applyStateGb === '06'" class="red">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
            <span v-else class="gray">{{ $gbNameKR('APPLY_STATE_GB',applicant.applyStateGb) }}</span>
          </td>
          <td>
            <div v-if="applicant.applyStateGb === '01'">
              <KSATextButton class="clickable black" text="서류합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '03')"/>
              &nbsp;
              <KSATextButton class="clickable black" text="서류불합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '04')"/>
            </div>
            <div v-else-if="applicant.applyStateGb === '03'">
              <KSATextButton class="clickable black" text="서류불합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '04')"/>
              &nbsp;
              <KSATextButton class="clickable black" text="최종합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '05')"/>
              &nbsp;
              <KSATextButton class="clickable black" text="최종불합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '06')"/>
            </div>
            <div v-else-if="applicant.applyStateGb === '04'">
              <KSATextButton class="clickable black" text="서류합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '03')"/>
            </div>
            <div v-else-if="applicant.applyStateGb === '05'">
              <KSATextButton class="clickable black" text="최종불합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '06')"/>
            </div>
            <div v-else-if="applicant.applyStateGb === '06'">
              <KSATextButton class="clickable black" text="최종합격" @click="onClickApplyStateGb(applicant.resumeId, applicant.jobNoticeSeq, '05')"/>
            </div>
            <div v-else>
              -
            </div>
          </td>
        </tr>
      </table>
      <div class="result-none" v-if="list.length === 0">
        입사지원 내역이 없습니다.
      </div>
    </div>
  </div>
</template>
<style scoped>
</style>
